import { Slot } from '@radix-ui/react-slot';
import type * as React from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends React.ComponentProps<'button'> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  asChild?: boolean;
}

export function Button({ className, variant = 'default', size = 'default', asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mesa-green/50',
        'disabled:pointer-events-none disabled:opacity-50',
        '[&_svg]:pointer-events-none [&_svg:not([class*=size-])]:size-4 [&_svg]:shrink-0',
        {
          'bg-mesa-green text-white hover:bg-mesa-green/90': variant === 'default',
          'bg-mesa-red text-white hover:bg-mesa-red/90': variant === 'destructive',
          'border border-mesa-border bg-transparent hover:bg-mesa-cream-dark': variant === 'outline',
          'bg-mesa-cream-dark hover:bg-mesa-cream-dark/80': variant === 'secondary',
          'hover:bg-mesa-cream-dark': variant === 'ghost',
          'text-mesa-green underline-offset-4 hover:underline': variant === 'link',
        },
        {
          'h-9 px-4 py-2': size === 'default',
          'h-8 px-3 text-sm': size === 'sm',
          'h-10 px-6': size === 'lg',
          'size-9': size === 'icon',
        },
        className
      )}
      {...props}
    />
  );
}
